import React from "react";
import { FileType, getFileType } from "../../utils";
import NotebookViewer from "./NotebookViewer";
import GitHubRepoRenderer from "components/Notebooks/GithubRepoRenderer";
import MarkdownRenderer from "./MarkdownViewer";
import { replaceBlobWithRaw } from "components/utils";

interface FileViewerProps {
  file?: string | null;
}

function getFileNameFromUrl(url: string): string {
  try {
    const pathParts = new URL(url).pathname.split("/").filter(Boolean);
    return pathParts[pathParts.length - 1] || "file";
  } catch {
    const pathParts = url.split("/").filter(Boolean);
    return pathParts[pathParts.length - 1] || "file";
  }
}

function getMarkdownReadmeUrl(url: string, type: FileType) {
  if (type === FileType.Markdown) {
    return replaceBlobWithRaw(url);
  }
  // Remove trailing slash and .git if present
  url = url.replace(/\/+$/, "");
  if (url.endsWith(".git")) {
    url = url.slice(0, -4);
  }

  // Extract username and repo name
  const parts = url.split("/");
  const username = parts[parts.length - 2];
  const repoName = parts[parts.length - 1];

  // Construct markdown URL format (points directly to the README in the GitHub UI)
  //raw.githubusercontent.com/${username}/${repoName}/main/
  const baseUrl = `https://raw.githubusercontent.com/${username}/${repoName}/main/`;
  return baseUrl + "README.md";
}

const FileViewer: React.FC<FileViewerProps> = ({ file }) => {
  const fileType = getFileType(file);

  return (
    <div>
      {/* Info text for file types */}
      {file && (
        <div className="mb-4">
          {(fileType === FileType.Colab || fileType === FileType.Notebook) && (
            <p className="text-xs text-secondary">
              This jupyter notebook will be present in the instance after
              deployment
            </p>
          )}
          {(fileType === FileType.GithubRepo ||
            fileType === FileType.GitlabRepo) && (
            <p className="text-xs text-secondary">
              This repository will be present in the instance after deployment
            </p>
          )}
        </div>
      )}

      {/* No file message */}
      {!file && (
        <>
          <hr className="w-full my-4 dark:border dark:border-zinc-800" />
          <div className="flex flex-col text-gray-900 dark:text-secondary">
            <p className="text-sm">
              This is a basic environment configuration with only hardware and
              container settings.
            </p>
            <p className="text-sm">No files or content were included.</p>
          </div>
        </>
      )}

      {/* Notebook/Colab viewer */}
      {file &&
        (fileType === FileType.Colab || fileType === FileType.Notebook) && (
          <>
            <hr className="w-full my-4 dark:border dark:border-zinc-800" />
            <div className="mb-4">
              <h1 className="text-lg font-semibold text-primary">
                {getFileNameFromUrl(file)}
              </h1>
            </div>
            <NotebookViewer file={file} />
          </>
        )}

      {/* GitHub repository viewer */}
      {file && fileType === FileType.GithubRepo && (
        <>
          <hr className="w-full my-4 dark:border dark:border-zinc-800" />
          <div className="mb-4">
            <h1 className="text-lg font-semibold text-primary">
              Repository Info
            </h1>
          </div>
          <GitHubRepoRenderer repoUrl={file} />
        </>
      )}

      {/* README viewer */}
      {file &&
        (fileType === FileType.GithubRepo ||
          fileType === FileType.GitlabRepo ||
          fileType === FileType.Markdown) && (
          <>
            <div className="mt-6 mb-4">
              <h1 className="text-lg font-semibold text-primary">README.md</h1>
            </div>
            <MarkdownRenderer
              markdownUrl={getMarkdownReadmeUrl(file, fileType)}
            />
          </>
        )}

      {/* Unrecognized file type: show the invalid notebook fallback */}
      {file && fileType === FileType.Unknown && (
        <>
          <hr className="w-full my-4 dark:border dark:border-zinc-800" />
          <div className="mb-4">
            <h1 className="text-lg font-semibold text-primary">
              {getFileNameFromUrl(file)}
            </h1>
          </div>
          <NotebookViewer file={file} />
        </>
      )}
    </div>
  );
};

export default FileViewer;
